import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { IAppPermissionsDomain } from 'graphql/types';
import { LoaderOverlay } from 'common/components/Loader';
import {
    useFrontendPermissions,
    RoleObjectType,
    ObjectAction,
} from 'user/hooks/useFrontendPermissions';
import { useAppPermissionsQuery } from 'user/hooks/usePermissions';
import { AppPermissionsContext } from 'user/contexts';
import { TPermissionsKey } from 'user/types';
import { FourOFourPage } from 'hydra/pages/FourOFourPage';

interface IBaseProps {
    children: React.ReactNode;
}

/**
 * Legacy mode: role-based permissions and/or the organisation app permissions
 * read from `currentUser.appPermissions` (via `AppPermissionsContext`).
 */
interface IRolePermissionGateProps extends IBaseProps {
    domain?: undefined;
    permission?: RoleObjectType;
    permissionAction?: ObjectAction;
    appPermission?: TPermissionsKey;
    appPermissionType?: string;
    appPermissionAction?: ObjectAction;
}

/**
 * New mode: grant-based permissions from the `appPermissions(domains:)` query
 * for a single `domain` (e.g. `viewBudgetManagement` under `Budget`).
 */
interface IDomainPermissionGateProps extends IBaseProps {
    domain: IAppPermissionsDomain;
    permission: string;
}

type IProps = IRolePermissionGateProps | IDomainPermissionGateProps;

export const PermissionGate = (props: IProps) => {
    const [translate] = useTranslation();
    const appPermissionsContext = useContext(AppPermissionsContext);

    // Legacy role-based permissions — only pass a role when not in domain mode.
    const rolePermission =
        props.domain === undefined ? props.permission : undefined;
    const { loading: loadingPermissions, ...restPermissions } =
        useFrontendPermissions(rolePermission);

    // New domain-based app permissions query — skipped outside domain mode.
    const { permissions: domainPermissions, loading: loadingDomain } =
        useAppPermissionsQuery(props.domain);

    const hasPermission = (() => {
        // New app permissions query mode.
        if (props.domain !== undefined) {
            return !!domainPermissions[props.permission];
        }

        // Role based frontend permissions.
        const hasRolePermission = (() => {
            if (!props.permission || !props.permissionAction) return true;

            const actionName =
                props.permissionAction.charAt(0).toUpperCase() +
                props.permissionAction.slice(1);
            const permissionName =
                `can${actionName}` as keyof typeof restPermissions;

            return !!restPermissions[permissionName];
        })();

        // Legacy organisation app permissions from `AppPermissionsContext`.
        // These are handled separately from role permissions since they are not
        // implemented everywhere yet, so when unused they should not block access.
        const hasAppPermission = (() => {
            if (
                !props.appPermission ||
                !props.appPermissionType ||
                !props.appPermissionAction
            ) {
                return true;
            }

            const actionName =
                props.appPermissionAction.charAt(0).toUpperCase() +
                props.appPermissionAction.slice(1);
            const appPermissionName = `can${actionName}`;

            const objectPermissions = appPermissionsContext?.[
                props.appPermission
            ] as
                | Record<string, Record<string, boolean | undefined>>
                | undefined;

            return !!objectPermissions?.[props.appPermissionType]?.[
                appPermissionName
            ];
        })();

        return hasRolePermission && hasAppPermission;
    })();

    if (loadingPermissions || loadingDomain) {
        return (
            <LoaderOverlay visible>
                {translate('loadingTexts.fetchingData')}
            </LoaderOverlay>
        );
    }

    if (!hasPermission) {
        return <FourOFourPage />;
    }

    return <>{props.children}</>;
};
